import React, { useMemo } from 'react';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';
import './expense-table.scss';

export interface TableColumn {
  id?: string;
  field_name: string;
  display_name: string;
  data_type: string;
  sub_data_type?: string;
  is_mandatory?: boolean;
  seq_no?: number;
  min_length?: number;
  max_length?: number;
}

export interface TableRowItem {
  id?: string;
  _rowKey: string;
  table_field_name: string;
  row_no: number;
  values: { [fieldName: string]: any };
}

interface ExpenseTableSectionProps {
  tableFieldName?: string;
  title?: string;
  subtitle?: string;
  columns: TableColumn[];
  rows: TableRowItem[];
  onChangeRows: (rows: TableRowItem[]) => void;
  customListMap?: any;
  isMandatory?: boolean;
  disabled?: boolean;
  validationErrors?: { [rowKey: string]: { [fieldName: string]: boolean } };
}

/**
 * Helper to identify Start Time column
 */
export const isStartTimeColumn = (col: TableColumn): boolean => {
  const name = (col.display_name || '').trim().toLowerCase();
  const fName = (col.field_name || '').trim().toLowerCase();
  return (
    name.includes('start time') ||
    fName.includes('start_time') ||
    name === 'start' ||
    fName === 'start'
  );
};

/**
 * Helper to identify End Time column
 */
export const isEndTimeColumn = (col: TableColumn): boolean => {
  const name = (col.display_name || '').trim().toLowerCase();
  const fName = (col.field_name || '').trim().toLowerCase();
  return (
    name.includes('end time') ||
    fName.includes('end_time') ||
    name === 'end' ||
    fName === 'end'
  );
};

/**
 * Helper to identify Duration column
 */
export const isDurationColumn = (col: TableColumn): boolean => {
  const name = (col.display_name || '').trim().toLowerCase();
  const fName = (col.field_name || '').trim().toLowerCase();
  return name.includes('duration') || fName.includes('duration');
};

/**
 * Parses various time representations into a moment object
 */
export const parseTimeToMoment = (val: any): moment.Moment | null => {
  if (!val) return null;
  if (val instanceof Date) {
    return moment(val);
  }
  if (typeof val === 'string') {
    const trimmed = val.trim();
    if (!trimmed) return null;
    const formats = [
      'hh:mm A',
      'hh:mm a',
      'h:mm A',
      'h:mm a',
      'HH:mm:ss',
      'HH:mm',
      'H:mm',
      moment.ISO_8601,
    ];
    const m = moment(trimmed, formats, false);
    if (m.isValid()) return m;
  }
  return null;
};

/**
 * Formats duration in minutes into a human-readable string (e.g. "2 hrs 30 mins", "1 hr", "45 mins")
 */
export const formatDuration = (totalMinutes: number): string => {
  if (totalMinutes <= 0 || isNaN(totalMinutes)) return '0 mins';
  const hours = Math.floor(totalMinutes / 60);
  const mins = Math.round(totalMinutes % 60);

  if (hours > 0 && mins > 0) {
    return `${hours} hr${hours > 1 ? 's' : ''} ${mins} min${mins > 1 ? 's' : ''}`;
  }
  if (hours > 0) {
    return `${hours} hr${hours > 1 ? 's' : ''}`;
  }
  return `${mins} min${mins > 1 ? 's' : ''}`;
};

/**
 * Calculates difference between start time and end time in minutes and formatted string
 */
export const calculateTimeDifference = (
  startTime: any,
  endTime: any,
): { minutes: number; formatted: string } => {
  if (!startTime || !endTime) {
    return { minutes: 0, formatted: '' };
  }

  const startM = parseTimeToMoment(startTime);
  const endM = parseTimeToMoment(endTime);

  if (!startM || !endM || !startM.isValid() || !endM.isValid()) {
    return { minutes: 0, formatted: '' };
  }

  let diffMinutes =
    endM.hours() * 60 +
    endM.minutes() -
    (startM.hours() * 60 + startM.minutes());

  // If end time is earlier than start time, assume it crosses midnight
  if (diffMinutes < 0) {
    diffMinutes += 24 * 60;
  }

  return {
    minutes: diffMinutes,
    formatted: formatDuration(diffMinutes),
  };
};

/**
 * Parses duration string/number into total minutes for summation
 */
export const parseDurationToMinutes = (val: any): number => {
  if (val === undefined || val === null || val === '') return 0;
  if (typeof val === 'number') {
    return val;
  }
  if (typeof val === 'string') {
    const trimmed = val.trim();
    if (!isNaN(Number(trimmed))) {
      return Number(trimmed);
    }

    let totalMinutes = 0;
    const hoursMatch = trimmed.match(/(\d+(?:\.\d+)?)\s*h(?:r|ours?)?/i);
    const minsMatch = trimmed.match(/(\d+(?:\.\d+)?)\s*m(?:in|inutes?)?/i);

    if (hoursMatch) {
      totalMinutes += parseFloat(hoursMatch[1]) * 60;
    }
    if (minsMatch) {
      totalMinutes += parseFloat(minsMatch[1]);
    }
    if (!hoursMatch && !minsMatch) {
      const parts = trimmed.split(':');
      if (parts.length === 2 && !isNaN(Number(parts[0])) && !isNaN(Number(parts[1]))) {
        totalMinutes = Number(parts[0]) * 60 + Number(parts[1]);
      }
    }
    return totalMinutes;
  }
  return 0;
};

/**
 * Normalizes rows loaded from backend API into our UI row model.
 */
export const normalizeSavedRowsToTableRows = (
  savedRows: any[],
  columns: TableColumn[],
  tableFieldName = 'linked_expenses',
): TableRowItem[] => {
  if (!Array.isArray(savedRows) || savedRows.length === 0) {
    return [];
  }

  return savedRows
    .filter(
      (row: any) =>
        (row.table_field_name || 'linked_expenses') === tableFieldName,
    )
    .map((row: any, index: number) => {
      const values: { [fieldName: string]: any } = {};

      columns.forEach((col) => {
        const fieldName = col.field_name;
        const displayName = col.display_name;

        let val: any = undefined;
        if (row.custom_fields && typeof row.custom_fields === 'object') {
          if (row.custom_fields[displayName] !== undefined) {
            val = row.custom_fields[displayName];
          } else if (row.custom_fields[fieldName] !== undefined) {
            val = row.custom_fields[fieldName];
          }
        }
        if (val === undefined && row[fieldName] !== undefined) {
          val = row[fieldName];
        }

        // Format value based on column data type
        if (col.data_type === 'picklist') {
          if (col.sub_data_type === 'multi_select') {
            if (Array.isArray(val)) {
              values[fieldName] = val.map((v: any) =>
                typeof v === 'object'
                  ? { value: v.id, label: v.text || v.name || v.label || v.display_name }
                  : { value: v, label: String(v) },
              );
            } else {
              values[fieldName] = [];
            }
          } else {
            if (val && typeof val === 'object') {
              values[fieldName] = {
                value: val.id,
                label: val.text || val.name || val.label || val.display_name,
              };
            } else if (val !== undefined && val !== null && val !== '') {
              values[fieldName] = val;
            } else {
              values[fieldName] = null;
            }
          }
        } else if (col.data_type === 'date') {
          if (val) {
            const parsed = new Date(val);
            values[fieldName] = !isNaN(parsed.getTime()) ? parsed : null;
          } else {
            values[fieldName] = null;
          }
        } else if (col.data_type === 'time' || col.sub_data_type === 'time') {
          if (val) {
            const m = moment(val, [
              moment.ISO_8601,
              'hh:mm a',
              'hh:mm A',
              'HH:mm',
              'HH:mm:ss',
              'YYYY-MM-DDTHH:mm:ssZ',
            ]);
            values[fieldName] = m.isValid() ? m.toDate() : null;
          } else {
            values[fieldName] = null;
          }
        } else if (
          col.data_type === 'radio' ||
          col.sub_data_type === 'boolean' ||
          col.sub_data_type === 'checkbox'
        ) {
          values[fieldName] = typeof val === 'boolean' ? val : val === 'true';
        } else {
          values[fieldName] = val !== undefined && val !== null ? val : '';
        }
      });

      return {
        id: row.id || undefined,
        _rowKey: row.id || `row_${Date.now()}_${index}_${Math.random()}`,
        row_no: row.row_no || index + 1,
        table_field_name: tableFieldName,
        values,
      };
    });
};

/**
 * Creates a blank new row initialized with proper default values.
 */
export const createEmptyTableRow = (
  columns: TableColumn[],
  tableFieldName = 'linked_expenses',
  rowNo = 1,
): TableRowItem => {
  const values: { [fieldName: string]: any } = {};
  columns.forEach((col) => {
    if (col.data_type === 'picklist') {
      values[col.field_name] =
        col.sub_data_type === 'multi_select' ? [] : null;
    } else if (
      col.data_type === 'radio' ||
      col.sub_data_type === 'boolean' ||
      col.sub_data_type === 'checkbox'
    ) {
      values[col.field_name] = false;
    } else if (col.data_type === 'date' || col.data_type === 'time' || col.sub_data_type === 'time') {
      values[col.field_name] = null;
    } else {
      values[col.field_name] = '';
    }
  });

  return {
    _rowKey: `row_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
    row_no: rowNo,
    table_field_name: tableFieldName,
    values,
  };
};

export const createInitialTableRow = createEmptyTableRow;

/**
 * Transforms UI rows into the payload structure expected by backend CreateExpenseEntryDto.
 */
export const formatTableRowsForPayload = (
  rows: TableRowItem[],
  columns: TableColumn[],
  tableFieldName = 'linked_expenses',
) => {
  if (!Array.isArray(rows)) return [];

  const startCol = columns.find(isStartTimeColumn);
  const endCol = columns.find(isEndTimeColumn);
  const durCol = columns.find(isDurationColumn);

  return rows.map((row, index) => {
    const custom_fields: any[] = [];

    // Auto-populate duration if duration column exists, start and end time exist, and duration is empty
    let effectiveDurationVal: string | undefined = undefined;
    if (durCol && startCol && endCol) {
      const s = row.values ? row.values[startCol.field_name] : undefined;
      const e = row.values ? row.values[endCol.field_name] : undefined;
      const existingDur = row.values ? row.values[durCol.field_name] : undefined;
      if ((existingDur === undefined || existingDur === null || existingDur === '') && s && e) {
        effectiveDurationVal = calculateTimeDifference(s, e).formatted;
      }
    }

    columns.forEach((col) => {
      const fieldName = col.field_name;
      let rawVal = row.values ? row.values[fieldName] : undefined;

      if (durCol && col.field_name === durCol.field_name && (rawVal === undefined || rawVal === null || rawVal === '')) {
        if (effectiveDurationVal) {
          rawVal = effectiveDurationVal;
        }
      }

      if (rawVal !== undefined && rawVal !== null && rawVal !== '') {
        let field_value: any = rawVal;

        if (isDurationColumn(col)) {
          if (col.data_type === 'numerical') {
            const minutes = parseDurationToMinutes(rawVal);
            field_value = Number((minutes / 60).toFixed(2));
          } else {
            field_value = String(rawVal);
          }
        } else if (col.data_type === 'picklist') {
          if (col.sub_data_type === 'multi_select') {
            field_value = Array.isArray(rawVal)
              ? rawVal
                  .map((v: any) =>
                    typeof v === 'object' ? v.value || v.id : Number(v),
                  )
                  .filter((id: any) => Boolean(id))
              : [];
          } else {
            field_value =
              typeof rawVal === 'object'
                ? rawVal.value || rawVal.id
                : Number(rawVal);
          }
        } else if (col.data_type === 'numerical') {
          field_value = Number(rawVal);
        } else if (col.data_type === 'date') {
          if (rawVal instanceof Date) {
            const year = rawVal.getFullYear();
            const month = String(rawVal.getMonth() + 1).padStart(2, '0');
            const day = String(rawVal.getDate()).padStart(2, '0');
            field_value = `${year}-${month}-${day}`;
          } else {
            field_value = String(rawVal).split('T')[0];
          }
        } else if (col.data_type === 'time' || col.sub_data_type === 'time') {
          if (rawVal instanceof Date) {
            field_value = moment(rawVal).format('hh:mm A');
          } else {
            const m = moment(rawVal, [
              moment.ISO_8601,
              'hh:mm a',
              'hh:mm A',
              'HH:mm',
              'HH:mm:ss',
              'YYYY-MM-DDTHH:mm:ssZ',
            ]);
            field_value = m.isValid() ? m.format('hh:mm A') : String(rawVal);
          }
        } else if (
          col.data_type === 'radio' ||
          col.sub_data_type === 'boolean' ||
          col.sub_data_type === 'checkbox'
        ) {
          field_value = Boolean(rawVal);
        } else {
          field_value = String(rawVal);
        }

        custom_fields.push({
          field_name: fieldName,
          field_value,
        });
      }
    });

    return {
      ...(row.id ? { id: row.id } : {}),
      table_field_name: row.table_field_name || tableFieldName,
      row_no: index + 1,
      custom_fields,
    };
  });
};

/**
 * Validates mandatory columns and overall table requirements.
 */
export const validateTableRows = (
  rows: TableRowItem[],
  columns: TableColumn[],
  isTableMandatory = false,
  tableDisplayName = 'Linked Expenses',
): { isValid: boolean; errorMessage?: string; errorLocation?: { rowKey: string; fieldName: string } } => {
  if (isTableMandatory && (!Array.isArray(rows) || rows.length === 0)) {
    return {
      isValid: false,
      errorMessage: `Please add at least one row in ${tableDisplayName}.`,
    };
  }

  for (let i = 0; i < (rows || []).length; i++) {
    const row = rows[i];
    for (const col of columns) {
      if (col.is_mandatory) {
        const val = row.values ? row.values[col.field_name] : undefined;
        const isEmpty =
          val === undefined ||
          val === null ||
          val === '' ||
          (Array.isArray(val) && val.length === 0);

        if (isEmpty) {
          return {
            isValid: false,
            errorMessage: `Row #${i + 1}: "${col.display_name}" is required.`,
            errorLocation: { rowKey: row._rowKey, fieldName: col.field_name },
          };
        }
      }
    }
  }

  return { isValid: true };
};

export default function ExpenseTableSection({
  tableFieldName = 'linked_expenses',
  title = 'Linked Expenses',
  subtitle = 'Add itemized records associated with this expense',
  columns = [],
  rows = [],
  onChangeRows,
  customListMap = {},
  isMandatory = false,
  disabled = false,
  validationErrors = {},
}: ExpenseTableSectionProps) {
  // Sort columns by seq_no
  const sortedColumns = useMemo(() => {
    return [...columns].sort((a, b) => (a.seq_no || 0) - (b.seq_no || 0));
  }, [columns]);

  // Identify Start Time, End Time, and Duration columns
  const startTimeCol = useMemo(
    () => sortedColumns.find(isStartTimeColumn),
    [sortedColumns],
  );
  const endTimeCol = useMemo(
    () => sortedColumns.find(isEndTimeColumn),
    [sortedColumns],
  );
  const durationCol = useMemo(
    () => sortedColumns.find(isDurationColumn),
    [sortedColumns],
  );
  const durationColIndex = useMemo(
    () => sortedColumns.findIndex(isDurationColumn),
    [sortedColumns],
  );
  const firstNumericColIndex = useMemo(
    () =>
      sortedColumns.findIndex(
        (col) => col.data_type === 'numerical' && !isDurationColumn(col),
      ),
    [sortedColumns],
  );

  const hasTotalsAfterDuration = useMemo(() => {
    if (durationColIndex === -1) return false;
    return sortedColumns
      .slice(durationColIndex + 1)
      .some((col) => col.data_type === 'numerical' && !isDurationColumn(col));
  }, [sortedColumns, durationColIndex]);

  const durationSpansActions = useMemo(() => {
    return durationColIndex !== -1 && !hasTotalsAfterDuration;
  }, [durationColIndex, hasTotalsAfterDuration]);

  const durationColSpan = useMemo(() => {
    if (!durationSpansActions) return 1;
    const remainingCols = sortedColumns.length - 1 - durationColIndex;
    const actionsCol = !disabled ? 1 : 0;
    return 1 + remainingCols + actionsCol;
  }, [durationSpansActions, sortedColumns.length, durationColIndex, disabled]);

  // Helper to get standardized minWidth for columns across thead, tbody, and tfoot
  const getColumnMinWidth = (col: TableColumn): string => {
    if (isDurationColumn(col)) return '180px';
    if (col.data_type === 'picklist') return '180px';
    if (col.data_type === 'date' || col.sub_data_type === 'date') return '150px';
    if (col.data_type === 'time' || col.sub_data_type === 'time') return '140px';
    if (col.data_type === 'email') return '160px';
    if (col.data_type === 'phone') return '140px';
    if (col.data_type === 'numerical') return '130px';
    return '140px';
  };

  // Calculate totals for numerical columns (excluding duration column if numerical)
  const numericTotals = useMemo(() => {
    const totals: { [fieldName: string]: number } = {};
    sortedColumns.forEach((col) => {
      if (col.data_type === 'numerical' && !isDurationColumn(col)) {
        const sum = rows.reduce((acc, row) => {
          const val = row.values ? Number(row.values[col.field_name]) : 0;
          return acc + (isNaN(val) ? 0 : val);
        }, 0);
        totals[col.field_name] = sum;
      }
    });
    return totals;
  }, [rows, sortedColumns]);

  // Calculate total duration across all rows in minutes
  const totalDurationMinutes = useMemo(() => {
    if (!durationCol) return 0;
    return rows.reduce((acc, row) => {
      let rowMinutes = 0;
      const durVal = row.values ? row.values[durationCol.field_name] : undefined;
      if (durVal !== undefined && durVal !== null && durVal !== '') {
        rowMinutes = parseDurationToMinutes(durVal);
      } else if (startTimeCol && endTimeCol) {
        const sVal = row.values ? row.values[startTimeCol.field_name] : undefined;
        const eVal = row.values ? row.values[endTimeCol.field_name] : undefined;
        if (sVal && eVal) {
          rowMinutes = calculateTimeDifference(sVal, eVal).minutes;
        }
      }
      return acc + rowMinutes;
    }, 0);
  }, [rows, durationCol, startTimeCol, endTimeCol]);

  // If no columns configured, don't render
  if (!sortedColumns.length) {
    return null;
  }

  // Helper to extract options for a picklist column
  const getOptionsForColumn = (col: TableColumn) => {
    if (!customListMap) return [];

    // Direct match by field_name
    if (Array.isArray(customListMap[col.field_name])) {
      return customListMap[col.field_name].map((item: any) => ({
        value: item.id,
        label: item.text || item.name || item.display_name,
      }));
    }

    // Match by key ending with ::field_name
    const matchingKey = Object.keys(customListMap).find(
      (key) => key.endsWith(`::${col.field_name}`) || key === col.field_name,
    );
    if (matchingKey && Array.isArray(customListMap[matchingKey])) {
      return customListMap[matchingKey].map((item: any) => ({
        value: item.id,
        label: item.text || item.name || item.display_name,
      }));
    }

    return [];
  };

  const handleAddRow = () => {
    if (disabled) return;
    const newRow = createEmptyTableRow(
      sortedColumns,
      tableFieldName,
      rows.length + 1,
    );
    onChangeRows([...rows, newRow]);
  };

  const handleDuplicateRow = (index: number) => {
    if (disabled) return;
    const targetRow = rows[index];
    const duplicatedRow: TableRowItem = {
      _rowKey: `row_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
      row_no: rows.length + 1,
      table_field_name: tableFieldName,
      values: { ...targetRow.values },
    };
    const updated = [...rows];
    updated.splice(index + 1, 0, duplicatedRow);
    // Re-index row numbers
    onChangeRows(
      updated.map((r, i) => ({
        ...r,
        row_no: i + 1,
      })),
    );
  };

  const handleDeleteRow = (index: number) => {
    if (disabled) return;
    const updated = rows.filter((_, i) => i !== index);
    onChangeRows(
      updated.map((r, i) => ({
        ...r,
        row_no: i + 1,
      })),
    );
  };

  const handleCellChange = (rowIndex: number, fieldName: string, value: any) => {
    if (disabled) return;
    const updated = [...rows];
    const currentRowValues = {
      ...updated[rowIndex].values,
      [fieldName]: value,
    };

    // Auto-calculate Duration if Start Time or End Time changes
    if (
      durationCol &&
      startTimeCol &&
      endTimeCol &&
      (fieldName === startTimeCol.field_name || fieldName === endTimeCol.field_name)
    ) {
      const sVal =
        fieldName === startTimeCol.field_name
          ? value
          : currentRowValues[startTimeCol.field_name];
      const eVal =
        fieldName === endTimeCol.field_name
          ? value
          : currentRowValues[endTimeCol.field_name];

      if (sVal && eVal) {
        const { formatted } = calculateTimeDifference(sVal, eVal);
        currentRowValues[durationCol.field_name] = formatted;
      } else {
        currentRowValues[durationCol.field_name] = '';
      }
    }

    updated[rowIndex] = {
      ...updated[rowIndex],
      values: currentRowValues,
    };
    onChangeRows(updated);
  };

  const hasNumericColumns = sortedColumns.some(
    (col) => col.data_type === 'numerical' && !isDurationColumn(col),
  );
  const hasFooterTotals = hasNumericColumns || Boolean(durationCol);

  return (
    <div className="expense-table-section">
      <div className="expense-table-section__header">
        <div className="expense-table-section__header-left">
          <h4
            className={`expense-table-section__header-title ${
              isMandatory
                ? 'expense-table-section__header-title--mandatory form-label-error'
                : ''
            }`}
          >
            {title}
            {isMandatory && (
              <span className="expense-table-section__header-mandatory">*</span>
            )}
          </h4>
          <span className="expense-table-section__header-badge">
            {rows.length} {rows.length === 1 ? 'Row' : 'Rows'}
          </span>
          <p className="expense-table-section__header-subtitle">{subtitle}</p>
        </div>

        {/* {!disabled && (
          <button
            type="button"
            className="expense-table-section__header-btn"
            onClick={handleAddRow}
          >
            <svg viewBox="0 0 24 24">
              <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
            </svg>
            Add Row
          </button>
        )} */}
      </div>

      {rows.length === 0 ? (
        <div className="expense-table-section__empty">
          <div className="expense-table-section__empty-icon">
            <svg
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 10h18M3 14h18m-9-4v8m-7 4h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          </div>
          <h5 className="expense-table-section__empty-title">
            No rows added yet
          </h5>
          <p className="expense-table-section__empty-desc">
            Add itemized breakdown entries for this expense.
          </p>
          {!disabled && (
            <button
              type="button"
              className="expense-table-section__empty-btn"
              onClick={handleAddRow}
            >
              + Add First Row
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="expense-table-section__table-wrapper">
            <table className="expense-table-section__table">
              <thead>
                <tr>
                  <th className="col-index">#</th>
                  {sortedColumns.map((col) => (
                    <th
                      key={col.field_name}
                      style={{
                        minWidth: getColumnMinWidth(col),
                        textAlign: isDurationColumn(col) ? 'center' : undefined,
                      }}
                    >
                      {col.display_name}
                      {col.is_mandatory && <span className="req-star">*</span>}
                    </th>
                  ))}
                  {!disabled && <th className="col-actions">Actions</th>}
                </tr>
              </thead>

              <tbody>
                {rows.map((row, rowIndex) => {
                  const rowErrors = validationErrors[row._rowKey] || {};

                  return (
                    <tr key={row._rowKey || rowIndex}>
                      <td className="cell-index">{rowIndex + 1}</td>

                      {sortedColumns.map((col) => {
                        const fieldName = col.field_name;
                        const cellValue = row.values ? row.values[fieldName] : undefined;
                        const hasError = Boolean(rowErrors[fieldName]);

                        return (
                          <td
                            key={fieldName}
                            style={{
                              minWidth: getColumnMinWidth(col),
                              boxSizing: 'border-box',
                            }}
                          >
                            {/* PICKLIST (SINGLE OR MULTI) */}
                            {col.data_type === 'picklist' ? (
                              <Select
                                isDisabled={disabled}
                                isMulti={col.sub_data_type === 'multi_select'}
                                options={getOptionsForColumn(col)}
                                value={
                                  col.sub_data_type === 'multi_select'
                                    ? cellValue || []
                                    : cellValue && typeof cellValue === 'object'
                                    ? cellValue
                                    : getOptionsForColumn(col).find(
                                        (opt: any) => opt.value === cellValue,
                                      ) || null
                                }
                                onChange={(selected: any) =>
                                  handleCellChange(
                                    rowIndex,
                                    fieldName,
                                    selected,
                                  )
                                }
                                placeholder={`Select ${col.display_name}`}
                                menuPortalTarget={document.body}
                                styles={{
                                  menuPortal: (base) => ({
                                    ...base,
                                    zIndex: 9999,
                                  }),
                                  control: (base, state) => ({
                                    ...base,
                                    minHeight: '36px',
                                    height: '36px',
                                    fontSize: '13px',
                                    borderRadius: '6px',
                                    minWidth: '160px',
                                    borderColor: hasError
                                      ? '#ef4444'
                                      : state.isFocused
                                      ? '#3c50e0'
                                      : '#cbd5e1',
                                    boxShadow: state.isFocused
                                      ? '0 0 0 2px rgba(60, 80, 224, 0.15)'
                                      : 'none',
                                    backgroundColor: hasError
                                      ? '#fff5f5'
                                      : '#ffffff',
                                  }),
                                  valueContainer: (base) => ({
                                    ...base,
                                    padding: '0 8px',
                                  }),
                                  indicatorsContainer: (base) => ({
                                    ...base,
                                    height: '34px',
                                  }),
                                }}
                              />
                            ) : col.data_type === 'time' ||
                              col.sub_data_type === 'time' ? (
                              /* TIME PICKER */
                              <div className="expense-table-datepicker">
                                <DatePicker
                                  disabled={disabled}
                                  selected={
                                    cellValue instanceof Date
                                      ? cellValue
                                      : cellValue
                                      ? moment(cellValue, [
                                          moment.ISO_8601,
                                          'hh:mm a',
                                          'hh:mm A',
                                          'HH:mm',
                                          'HH:mm:ss',
                                          'YYYY-MM-DDTHH:mm:ssZ',
                                        ]).isValid()
                                        ? moment(cellValue, [
                                            moment.ISO_8601,
                                            'hh:mm a',
                                            'hh:mm A',
                                            'HH:mm',
                                            'HH:mm:ss',
                                            'YYYY-MM-DDTHH:mm:ssZ',
                                          ]).toDate()
                                        : null
                                      : null
                                  }
                                  onChange={(date: Date | null) =>
                                    handleCellChange(rowIndex, fieldName, date)
                                  }
                                  showTimeSelect
                                  showTimeSelectOnly
                                  timeIntervals={1}
                                  timeCaption="Time"
                                  dateFormat="hh:mm aa"
                                  placeholderText="hh:mm aa"
                                  className={`expense-table-section__input ${
                                    hasError ? 'has-error' : ''
                                  }`}
                                  portalId="root"
                                />
                              </div>
                            ) : col.data_type === 'date' ? (
                              /* DATE PICKER */
                              <div className="expense-table-datepicker">
                                <DatePicker
                                  disabled={disabled}
                                  selected={
                                    cellValue instanceof Date
                                      ? cellValue
                                      : cellValue
                                      ? new Date(cellValue)
                                      : null
                                  }
                                  onChange={(date: Date | null) =>
                                    handleCellChange(rowIndex, fieldName, date)
                                  }
                                  dateFormat="yyyy-MM-dd"
                                  placeholderText="YYYY-MM-DD"
                                  className={`expense-table-section__input ${
                                    hasError ? 'has-error' : ''
                                  }`}
                                  portalId="root"
                                />
                              </div>
                            ) : col.data_type === 'radio' ||
                              col.sub_data_type === 'boolean' ||
                              col.sub_data_type === 'checkbox' ? (
                              /* CHECKBOX / BOOLEAN */
                              <div className="expense-table-section__checkbox-wrap">
                                <input
                                  type="checkbox"
                                  disabled={disabled}
                                  checked={Boolean(cellValue)}
                                  onChange={(e) =>
                                    handleCellChange(
                                      rowIndex,
                                      fieldName,
                                      e.target.checked,
                                    )
                                  }
                                />
                              </div>
                            ) : isDurationColumn(col) ? (
                              /* DURATION INPUT (AUTO-CALCULATED OR MANUAL) */
                              <input
                                type="text"
                                disabled={disabled}
                                value={
                                  cellValue !== undefined && cellValue !== null
                                    ? cellValue
                                    : ''
                                }
                                onChange={(e) =>
                                  handleCellChange(
                                    rowIndex,
                                    fieldName,
                                    e.target.value,
                                  )
                                }
                                placeholder={
                                  startTimeCol && endTimeCol
                                    ? 'Auto-calculated'
                                    : `Enter ${col.display_name}`
                                }
                                className={`expense-table-section__input ${
                                  hasError ? 'has-error' : ''
                                }`}
                                style={{
                                  width: '100%',
                                  maxWidth: '100%',
                                  boxSizing: 'border-box',
                                  textAlign: 'center',
                                  backgroundColor:
                                    startTimeCol && endTimeCol
                                      ? '#f8fafc'
                                      : '#ffffff',
                                  fontWeight: 500,
                                }}
                              />
                            ) : col.data_type === 'numerical' ? (
                              /* NUMERICAL INPUT */
                              <input
                                type="number"
                                step="any"
                                disabled={disabled}
                                value={
                                  cellValue !== undefined &&
                                  cellValue !== null
                                    ? cellValue
                                    : ''
                                }
                                onChange={(e) =>
                                  handleCellChange(
                                    rowIndex,
                                    fieldName,
                                    e.target.value,
                                  )
                                }
                                placeholder="0.00"
                                className={`expense-table-section__input ${
                                  hasError ? 'has-error' : ''
                                }`}
                                style={{
                                  width: '100%',
                                  maxWidth: '100%',
                                  boxSizing: 'border-box',
                                  textAlign: 'right',
                                }}
                              />
                            ) : col.data_type === 'email' ? (
                              /* EMAIL INPUT */
                              <input
                                type="email"
                                disabled={disabled}
                                value={cellValue || ''}
                                onChange={(e) =>
                                  handleCellChange(
                                    rowIndex,
                                    fieldName,
                                    e.target.value,
                                  )
                                }
                                placeholder="name@example.com"
                                className={`expense-table-section__input ${
                                  hasError ? 'has-error' : ''
                                }`}
                                style={{
                                  width: '100%',
                                  maxWidth: '100%',
                                  boxSizing: 'border-box',
                                }}
                              />
                            ) : col.data_type === 'phone' ? (
                              /* PHONE INPUT */
                              <input
                                type="tel"
                                disabled={disabled}
                                value={cellValue || ''}
                                onChange={(e) =>
                                  handleCellChange(
                                    rowIndex,
                                    fieldName,
                                    e.target.value,
                                  )
                                }
                                placeholder="+1 (555) 000-0000"
                                className={`expense-table-section__input ${
                                  hasError ? 'has-error' : ''
                                }`}
                                style={{
                                  width: '100%',
                                  maxWidth: '100%',
                                  boxSizing: 'border-box',
                                }}
                              />
                            ) : (
                              /* STANDARD TEXT INPUT */
                              <input
                                type="text"
                                disabled={disabled}
                                value={cellValue || ''}
                                onChange={(e) =>
                                  handleCellChange(
                                    rowIndex,
                                    fieldName,
                                    e.target.value,
                                  )
                                }
                                placeholder={`Enter ${col.display_name}`}
                                className={`expense-table-section__input ${
                                  hasError ? 'has-error' : ''
                                }`}
                                style={{
                                  width: '100%',
                                  maxWidth: '100%',
                                  boxSizing: 'border-box',
                                }}
                              />
                            )}
                          </td>
                        );
                      })}

                      {/* ROW ACTIONS */}
                      {!disabled && (
                        <td className="cell-actions">
                          <div className="action-btn-group">
                            <button
                              type="button"
                              className="row-action-btn"
                              title="Duplicate Row"
                              onClick={() => handleDuplicateRow(rowIndex)}
                            >
                              <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <rect
                                  x="9"
                                  y="9"
                                  width="13"
                                  height="13"
                                  rx="2"
                                  ry="2"
                                />
                                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                              </svg>
                            </button>

                            <button
                              type="button"
                              className="row-action-btn delete-btn"
                              title="Delete Row"
                              onClick={() => handleDeleteRow(rowIndex)}
                            >
                              <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>

              {/* FOOTER TOTALS */}
              {hasFooterTotals && (
                <tfoot>
                  <tr>
                    <td className="total-label">
                      {durationColIndex === 0 ? 'Total Hours:' : ''}
                    </td>
                    {sortedColumns.map((col, colIndex) => {
                      // If duration spans across remaining columns and Actions, skip rendering extra cells after duration
                      if (durationSpansActions && colIndex > durationColIndex) {
                        return null;
                      }

                      // Column immediately before Duration column
                      if (
                        durationColIndex !== -1 &&
                        colIndex === durationColIndex - 1
                      ) {
                        return (
                          <td
                            key={col.field_name}
                            className="total-label"
                            style={{
                              textAlign: 'right',
                              fontWeight: 700,
                              color: '#1e293b',
                              fontSize: '13px',
                              whiteSpace: 'nowrap',
                              overflow: 'visible',
                            }}
                          >
                            Total Hours:
                          </td>
                        );
                      }

                      // Column immediately before first numeric column (if no duration column)
                      if (
                        durationColIndex === -1 &&
                        firstNumericColIndex !== -1 &&
                        colIndex === firstNumericColIndex - 1
                      ) {
                        return (
                          <td
                            key={col.field_name}
                            className="total-label"
                            style={{
                              textAlign: 'right',
                              fontWeight: 700,
                              color: '#1e293b',
                              fontSize: '13px',
                              whiteSpace: 'nowrap',
                              overflow: 'visible',
                            }}
                          >
                            Total:
                          </td>
                        );
                      }

                      // Column immediately before numeric column (if duration column exists but isn't right before numeric)
                      if (
                        durationColIndex !== -1 &&
                        firstNumericColIndex !== -1 &&
                        colIndex === firstNumericColIndex - 1 &&
                        colIndex !== durationColIndex &&
                        colIndex !== durationColIndex - 1
                      ) {
                        return (
                          <td
                            key={col.field_name}
                            className="total-label"
                            style={{
                              textAlign: 'right',
                              fontWeight: 700,
                              color: '#1e293b',
                              fontSize: '13px',
                              whiteSpace: 'nowrap',
                              overflow: 'visible',
                            }}
                          >
                            Total:
                          </td>
                        );
                      }

                      // Duration total column (spans into Actions column to use empty space)
                      if (isDurationColumn(col)) {
                        const totalHoursDecimal = (
                          totalDurationMinutes / 60
                        ).toFixed(2);
                        const formattedDuration =
                          formatDuration(totalDurationMinutes);
                        return (
                          <td
                            key={col.field_name}
                            colSpan={durationColSpan}
                            className="total-value total-duration-value"
                            style={{
                              textAlign: 'left',
                              paddingLeft: '12px',
                              whiteSpace: 'nowrap',
                              overflow: 'visible',
                              textOverflow: 'clip',
                            }}
                          >
                            <span
                              style={{
                                display: 'inline',
                                color: '#0f172a',
                                fontWeight: 700,
                                fontSize: '13px',
                                overflow: 'visible',
                                textOverflow: 'clip',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {totalHoursDecimal} hrs
                            </span>
                            {totalDurationMinutes > 0 && (
                              <span
                                className="total-duration-formatted"
                                style={{
                                  display: 'inline',
                                  fontSize: '13px',
                                  fontWeight: 700,
                                  color: '#0f172a',
                                  marginLeft: '8px',
                                  overflow: 'visible',
                                  textOverflow: 'clip',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                ({formattedDuration})
                              </span>
                            )}
                          </td>
                        );
                      }

                      // Numerical total column
                      if (col.data_type === 'numerical') {
                        const total = numericTotals[col.field_name] || 0;
                        return (
                          <td
                            key={col.field_name}
                            className="total-value"
                            style={{
                              textAlign: 'right',
                              fontWeight: 700,
                              color: '#0f172a',
                              fontSize: '13px',
                            }}
                          >
                            {total.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </td>
                        );
                      }

                      return <td key={col.field_name}></td>;
                    })}
                    {!disabled && !durationSpansActions && <td></td>}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {!disabled && (
            <div className="expense-table-section__footer-bar">
              <button
                type="button"
                className="add-more-btn"
                onClick={handleAddRow}
              >
                {/* <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                </svg> */}
                + Add Another Row
              </button>
              {/* <span className="row-summary">
                {rows.length} {rows.length === 1 ? 'row' : 'rows'} entered
              </span> */}
            </div>
          )}
        </>
      )}
    </div>
  );
}
